import Vue from 'vue';
import Router, { Route } from 'vue-router';
import * as _ from 'lodash';

import { Store as VuexStore } from 'vuex';
import { Store } from '@/store';

import LoadingComponent from '@/components/pages/LoadingComponent.vue';

function lazyLoadView(AsyncView: Promise<typeof import('*.vue')>): any {
  const AsyncHandler = () => ({
    component: AsyncView,
    loading: LoadingComponent,
    delay: 100,
  });
  return Promise.resolve({
    functional: true,
    render(h: any, { data, children }: any) {
      return h(AsyncHandler, data, children);
    },
  });
}

import {
  redirectToBuildingsListIfUserIsLoggedInNavigationGuard,
  validateUserIsLoggedInAndIsInOrganization,
  validateUserIsLoggedInIsInOrganizationAndSelectedBuilding,
  validateUserIsLoggedInIsInOrganizationForAuth,
  redirectToBuildingsListOrBuildingIfUserIsLoggedInNavigationGuard,
  validateUserIsLoggedIn,
  validateUserIsEnabledScenarioFeature,
} from '@/router/helpers/navigationGuards';
import { saveLocalePreference, localeIsSupported } from '@/interfaces/Locale';
import { getValueFromRouteQuery } from '@/helpers/vueRouteQueryHelpers';

// Workaround for catch all error throw by navigation guard redirection
// Ref:
// - https://github.com/vuejs/vue-router/issues/2881#issuecomment-520554378
// - https://router.vuejs.org/guide/advanced/navigation-failures.html#detecting-navigation-failures
const originalPush = Router.prototype.push;
// @ts-ignore
Router.prototype.push = function push(location, onResolve, onReject) {
  if (onResolve || onReject)
    return originalPush.call(this, location, onResolve, onReject);
  // @ts-ignore
  return originalPush.call(this, location).catch((err: any) => {
    if (Router.isNavigationFailure(err)) {
      // resolve err
      return err;
    }
    // rethrow error
    return Promise.reject(err);
  });
};
Vue.use(Router);

function parseAfterSuccessToPredefinedNextLocationWithQueryParameters(
  route: Route,
): Object {
  if (route.query.after_success != undefined) {
    return {
      predefinedNextLocation: {
        name: route.query.after_success,
        query: _.omit(route.query, 'after_success'),
        params: route.params,
      }
    };
  }
  return {};
}

export default async (store: VuexStore<Store>) => {

  const router = new Router({
    mode: 'hash',
    routes: [
      {
        path: '/welcome',
        name: 'Welcome',
        component: () => lazyLoadView(import('@/components/pages/welcome/SigninPage.vue')),
        beforeEnter: redirectToBuildingsListOrBuildingIfUserIsLoggedInNavigationGuard(store),
        props: parseAfterSuccessToPredefinedNextLocationWithQueryParameters,
        meta: {
          responsiveLayout: true,
        },
      },
      {
        path: '/signup',
        name: 'WelcomeSignup',
        component: () => lazyLoadView(import('@/components/pages/welcome/SignupPage.vue')),
        meta: {
          responsiveLayout: true,
        },
      },
      {
        path: '/organization-details',
        name: 'FillOrganizationDetails',
        component: () => lazyLoadView(import('@/components/pages/welcome/OrganizationDetailsPage.vue')),
        props: parseAfterSuccessToPredefinedNextLocationWithQueryParameters,
        meta: {
          responsiveLayout: true,
        },
      },
      {
        path: '/activate',
        name: 'AccountActivation',
        component: () => lazyLoadView(import('@/components/pages/welcome/AccountActivationPage.vue')),
        meta: {
          responsiveLayout: true,
        },
      },
      {
        path: '/accept-invitation',
        name: 'AcceptOrganizationInvitation',
        component: () => lazyLoadView(import('@/components/pages/welcome/AcceptOrganizationInvitationPage.vue')),
        props: (route: Route) => {
          return {
            code: route.query.code,
          };
        },
        meta: {
          responsiveLayout: true,
        },
      },
      {
        path: '/restore-password',
        name: 'RestorePasswordPage',
        component: () => lazyLoadView(import('@/components/pages/welcome/RestorePasswordPage.vue')),
        beforeEnter: redirectToBuildingsListIfUserIsLoggedInNavigationGuard(store),
        meta: {
          responsiveLayout: true,
        },
      },
      {
        path: '/reset-password',
        name: 'ResetPasswordPage',
        component: () => lazyLoadView(import('@/components/pages/welcome/ResetPasswordPage.vue')),
        meta: {
          responsiveLayout: true,
        },
      },
      {
        path: '/oauth/authorize',
        name: 'AuthorizeExternalServicePage',
        component: () => lazyLoadView(import('@/components/pages/oauth2/AuthorizeExternalServicePage.vue')),
        beforeEnter: validateUserIsLoggedInIsInOrganizationForAuth(store),
        meta: {
          responsiveLayout: true,
        },
      },
      {
        path: '/buildings',
        name: 'Buildings',
        component: () => lazyLoadView(import('@/components/pages/createOrSelectABuilding/CreateOrSelectABuildingPage.vue')),
        beforeEnter: validateUserIsLoggedInAndIsInOrganization(store),
        meta: {
          hidesNavbar: true,
        },
        children: [
          {
            path: 'building-list',
            name: 'BuildingList',
            component: () => lazyLoadView(import('@/components/pages/buildingList/BuildingList.vue')),
            beforeEnter: validateUserIsLoggedInAndIsInOrganization(store),
          },
          {
            path: 'create-a-building',
            name: 'CreateABuilding',
            component: () => lazyLoadView(import('@/components/pages/createOrSelectABuilding/subcomponents/createABuilding/CreateABuilding.vue')),
            beforeEnter: validateUserIsLoggedInAndIsInOrganization(store),
          },
        ],
      },
      {
        path: '/building',
        name: 'Building',
        component: () => lazyLoadView(import('@/components/pages/building/page/BuildingPage.vue')),
        beforeEnter: validateUserIsLoggedInIsInOrganizationAndSelectedBuilding(store),
      },
      {
        path: '/scenes',
        name: 'Scenes',
        component: () => lazyLoadView(import('@/components/pages/scenes/ScenesPage.vue')),
        beforeEnter: validateUserIsLoggedInIsInOrganizationAndSelectedBuilding(store),
      },
      {
        path: '/schedules',
        name: 'Schedules',
        component: () => lazyLoadView(import('@/components/pages/schedules/SchedulesPage.vue')),
        beforeEnter: validateUserIsLoggedInIsInOrganizationAndSelectedBuilding(store),
      },
      {
        path: '/rhythms',
        name: 'Rhythms',
        component: () => lazyLoadView(import('@/components/pages/rhythms/RhythmsPage.vue')),
        beforeEnter: validateUserIsLoggedInIsInOrganizationAndSelectedBuilding(store),
      },
      {
        path: '/settings',
        name: 'Settings',
        redirect: {name: 'BuildingStructure'},
        component: () => lazyLoadView(import('@/components/pages/settings/SettingsPage.vue')),
        beforeEnter: validateUserIsLoggedInIsInOrganizationAndSelectedBuilding(store),
        children: [
          {
            path: 'organization',
            name: 'Organization',
            component: () => lazyLoadView(import('@/components/pages/organization/OrganizationPage.vue')),
          },
          {
            path: 'building-structure',
            name: 'BuildingStructure',
            component: () => lazyLoadView(import('@/components/pages/buildingStructure/BuildingStructurePage.vue')),
            beforeEnter: validateUserIsLoggedInIsInOrganizationAndSelectedBuilding(store),
          },
          {
            path: 'setup-commissioning',
            name: 'SetupCommissioning',
            component: () => lazyLoadView(import('@/components/pages/setupCommissioning/SetupCommissioningPage.vue')),
            beforeEnter: validateUserIsLoggedInIsInOrganizationAndSelectedBuilding(store),
          },
          {
            path: 'wifi-credentials',
            name: 'WiFiCredentials',
            component: () => lazyLoadView(import('@/components/pages/wiFiCredentials/WiFiCredentialsPage.vue')),
            beforeEnter: validateUserIsLoggedInIsInOrganizationAndSelectedBuilding(store),
          },
          {
            path: 'floor-plan',
            name: 'FloorPlan',
            component: () => lazyLoadView(import('@/components/pages/floorPlan/FloorPlanPage.vue')),
            beforeEnter: validateUserIsLoggedInIsInOrganizationAndSelectedBuilding(store),
          },
          {
            path: 'managing-licenses',
            name: 'ManagingLicenses',
            component: () => lazyLoadView(import('@/components/pages/licenses/LicensesPage.vue')),
            beforeEnter: validateUserIsLoggedInIsInOrganizationAndSelectedBuilding(store),
          },{
            path: 'members',
            name: 'Members',
            component: () => lazyLoadView(import('@/components/pages/members/MembersPage.vue')),
            beforeEnter: validateUserIsLoggedInIsInOrganizationAndSelectedBuilding(store),
          },{
            path: 'energy-monitoring',
            name: 'EnergyMonitoring',
            component: () => lazyLoadView(import('@/components/pages/energyMonitoring/EnergyMonitoringPage.vue')),
            beforeEnter: validateUserIsLoggedInIsInOrganizationAndSelectedBuilding(store),
          },{
            path: 'preventive-maintenance',
            name: 'PreventiveMaintenance',
            component: () => lazyLoadView(import('@/components/pages/preventiveMaintenance/PreventiveMaintenancePage.vue')),
            beforeEnter: validateUserIsLoggedInIsInOrganizationAndSelectedBuilding(store),
          },
          {
            path: 'data-export',
            name: 'DataExport',
            component: () => lazyLoadView(import('@/components/pages/dataExport/DataExportPage.vue')),
            beforeEnter: validateUserIsLoggedInIsInOrganizationAndSelectedBuilding(store),
          },
          {
            path: 'managing-custom-scenarios',
            name: 'ManagingCustomScenarios',
            component: () => lazyLoadView(import('@/components/pages/customScenarios/CustomScenariosPage.vue')),
            beforeEnter: validateUserIsEnabledScenarioFeature(store),
          },
        ]
      },
      {
        path: '/faq',
        name: 'FAQ',
        component: () => lazyLoadView(import('@/components/pages/faq/FaqPage.vue')),
        beforeEnter: validateUserIsLoggedInIsInOrganizationAndSelectedBuilding(store),
      },
      {
        path: '/wall-panel/:roomId',
        name: 'WallPanelWithRoomId',
        component: () => lazyLoadView(import('@/components/pages/wallPanelView/WallPanelViewPage.vue')),
        beforeEnter: validateUserIsLoggedInIsInOrganizationAndSelectedBuilding(store),
      },
      {
        path: '/wall-panel',
        name: 'WallPanel',
        meta: {
          hidesNavbar: true,
        },
        component: () => lazyLoadView(import('@/components/pages/wallPanelView/WallPanelViewPage.vue')),
        beforeEnter: validateUserIsLoggedInIsInOrganizationAndSelectedBuilding(store),
      },
      {
        path: '/profile',
        name: 'Profile',
        component: () => lazyLoadView(import('@/components/pages/profile/Profile.vue')),
        beforeEnter: validateUserIsLoggedIn(store),
      },
      {
        path: '*',
        redirect: '/welcome',
      },
    ],
  });

  router.beforeEach(async (to, _from, next) => {
    let locationUpdatedCount = 0;

    // For any action that mutated location object (updating query, param, path, ...etc)
    // increment the count so that the `next()` function will receive an updated Location object when called.
    locationUpdatedCount += consumeLocalePreferenceInQuery(to);

    next(
      locationUpdatedCount > 0
        ? {
            name: to.name || undefined, // if null, send undefined
            path: to.path,
            hash: to.hash,
            query: to.query,
            params: to.params,
            replace: true,
          }
        : undefined,
    );
  });

  return router;
};

/**
 * Switch to locale specified in query, if available.
 * @param to Current route to check.
 * @returns Route object mutate count.
 */
function consumeLocalePreferenceInQuery(to: Route): number {
  const localeInQuery = getValueFromRouteQuery(to, 'locale');
  if (!localeInQuery) {
    return 0;
  }
  const locale = localeIsSupported(localeInQuery);
  if (locale) {
    saveLocalePreference(locale);
  }
  // Remove from query no matter it is supported or not
  delete to.query['locale'];
  return 1;
}
